import React from 'react';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Tag,
  Typography,
  DatePicker,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { Gender } from '../../../../constants/gender';
import { PatientCareLevel } from '../../../../constants/careLevel';
import { IndependentLifeLevel } from '../../../../constants/independentLifeLevel';
import { DentalIntervention } from '../../../../constants/dentalIntervention';
import { useTagFindAll } from '../../../../libs/ohat-api';
import { FeatureGate } from '../../../components/common/FeatureGate';
import { useTranslation } from 'react-i18next';
import { ALL_QUESTIONS } from '../../../../constants/question';
import { usePlan } from '../../../../hooks/usePlan';

const { Option } = Select;

/** 合計点の下限・上限（q1〜q8 の和） */
export const TOTAL_SCORE_MIN = 0;
export const TOTAL_SCORE_MAX = 16;

/**
 * 項目別スコア絞り込みの「1以上」を表すフォーム値。
 * 0/1/2（一致条件）の数値と衝突しないよう文字列で表現する。
 */
export const SCORE_AT_LEAST_1 = 'gte1';

/**
 * 項目別スコアの絞り込み条件。
 * 0 / 1 / 2 は一致、SCORE_AT_LEAST_1 はスコア1以上。
 */
export type LatestScoreFilterValue = 0 | 1 | 2 | typeof SCORE_AT_LEAST_1;

/** 合計点の比較条件。eq=一致 / gte=以上 / lte=以下 */
export type TotalScoreCondition = 'eq' | 'gte' | 'lte';

export type SearchFormValues = {
  name?: string;
  gender?: string;
  careLevel?: string;
  lifeLevel?: string;
  dentalIntervention?: string;
  tagIds?: string[];
  evaluationDate?: any;
  ascSort?: { key: string; order: number }[];
  descSort?: { key: string; order: number }[];
  latestScoreQ1?: LatestScoreFilterValue;
  latestScoreQ2?: LatestScoreFilterValue;
  latestScoreQ3?: LatestScoreFilterValue;
  latestScoreQ4?: LatestScoreFilterValue;
  latestScoreQ5?: LatestScoreFilterValue;
  latestScoreQ6?: LatestScoreFilterValue;
  latestScoreQ7?: LatestScoreFilterValue;
  latestScoreQ8?: LatestScoreFilterValue;
  /** 最新評価の合計点（未入力なら合計点での絞り込みは行わない） */
  latestTotalScore?: number;
  latestTotalScoreCondition?: TotalScoreCondition;
};

type SearchFormProps = {
  onSearch: (values: SearchFormValues) => void;
  onReset: () => void;
  onClose: () => void;
};

export const PatientSearchForm: React.FC<SearchFormProps> = ({
  onSearch,
  onReset,
  onClose,
}) => {
  const [form] = Form.useForm();
  const { data: tags } = useTagFindAll();
  const { t, i18n } = useTranslation();
  const { canUse } = usePlan();
  // 最新OHATスコア関連（項目別の絞り込み・合計点ソート）はライトプラン限定
  const canUseLatestScore = canUse('advanced_search');

  const sortFields = [
    { key: 'name', label: t('search.name') },
    { key: 'age', label: t('search.age') },
    { key: 'careLevelForSort', label: t('search.careLevel') },
    { key: 'independentLifeLevelForSort', label: t('search.lifeLevel') },
    { key: 'latestOhatScoreEvaluatedAtForSort', label: t('search.lastAssessDate') },
    { key: 'ohatScoreEvaluatedCount', label: t('search.assessCount') },
    // 最新OHATスコア（合計点）ソートは最新OHATスコア機能の一部のため無料プランでは非表示
    ...(canUseLatestScore
      ? [{ key: 'latestScore', label: t('search.latestScoreSort') }]
      : []),
  ];

  const resetSearch = () => {
    form.resetFields();
    onReset();
  };

  const handleSortChange = (value: string[], field: 'ascSort' | 'descSort') => {
    const otherField = field === 'ascSort' ? 'descSort' : 'ascSort';
    const currentValues = form.getFieldValue(field) || [];
    const otherValues = form.getFieldValue(otherField) || [];

    // 現在の順序を保持
    const existingOrders = [...currentValues, ...otherValues]
      .sort((a, b) => a.order - b.order)
      .map((item) => ({
        key: item.key,
        order: item.order,
      }));

    // 他方フィールドから重複項目を削除
    const updatedOtherValues = otherValues.filter(
      (item: { key: string }) => !value.includes(item.key),
    );

    // 現在のフィールドにセットされた値を更新
    const updatedCurrentValues = value.map((key) => {
      // 既存の順序を探す
      const existingOrder = existingOrders.find(
        (item) => item.key === key,
      )?.order;
      const existingItem = currentValues.find(
        (item: { key: string }) => item.key === key,
      );

      return (
        existingItem || {
          key,
          order:
            existingOrder ||
            Math.max(...existingOrders.map((item) => item.order || 0), 0) + 1,
        }
      );
    });

    // 全てのソート項目を結合して順序を保持しつつ新しい項目を末尾に追加
    const allSortValues = [...updatedOtherValues, ...updatedCurrentValues].sort(
      (a, b) => (a.order || Infinity) - (b.order || Infinity),
    );

    // orderを1から振り直す
    const recalculatedSortValues = allSortValues.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    // 再計算された値を現在のフィールドと他方のフィールドに振り分ける
    const recalculatedCurrentValues = recalculatedSortValues.filter((item) =>
      value.includes(item.key),
    );
    const recalculatedOtherValues = recalculatedSortValues.filter(
      (item) => !value.includes(item.key),
    );

    // フォームの値を更新
    form.setFieldsValue({
      [field]: recalculatedCurrentValues,
      [otherField]: recalculatedOtherValues,
    });
  };

  const handleSubmit = (values: any) => {
    // フォームの値を整形して onSearch に渡す
    const formattedValues = {
      ...values,
      ascSort: values.ascSort || [],
      descSort: values.descSort || [],
    };
    onSearch(formattedValues);
  };

  return (
    <Form form={form} onFinish={handleSubmit} layout="vertical">
      <div
        style={{
          border: '1px solid #f0f0f0',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#fafafa',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: '16px' }}>
          <Form.Item name="name" label={t('search.name')}>
            <Input
              placeholder={t('search.namePlaceholder')}
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item name="gender" label={t('search.gender')}>
            <Select
              placeholder={t('search.selectGender')}
              style={{ width: 120 }}
            >
              <Option value={Gender.male.key}>
                {Gender.male.getLabel(i18n.language)}
              </Option>
              <Option value={Gender.female.key}>
                {Gender.female.getLabel(i18n.language)}
              </Option>
            </Select>
          </Form.Item>
          <Form.Item name="careLevel" label={t('search.careLevel')}>
            <Select
              placeholder={t('search.selectCareLevel')}
              style={{ width: 160 }}
            >
              {Object.values(PatientCareLevel).map((level) => (
                <Option key={level.key} value={level.key}>
                  {level.getLabel(i18n.language)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="lifeLevel" label={t('search.lifeLevel')}>
            <Select
              placeholder={t('search.selectLifeLevel')}
              style={{ width: 200 }}
            >
              {Object.values(IndependentLifeLevel).map((level) => (
                <Option key={level.key} value={level.key}>
                  {level.getLabel(i18n.language)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="dentalIntervention" label={t('search.dentalIntervention')}>
            <Select
              placeholder={t('search.selectDental')}
              style={{ width: 160 }}
            >
              {Object.values(DentalIntervention).map((intervention) => (
                <Option key={intervention.key} value={intervention.key}>
                  {intervention.getLabel(i18n.language)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {/* アプリ全体の antd locale は変えていないため、既定の英語表記にならないよう
              この RangePicker にだけ i18n のプレースホルダーを渡す。 */}
          <Form.Item name="evaluationDate" label={t('search.evaluationPeriod')}>
            <DatePicker.RangePicker
              style={{ width: 260 }}
              placeholder={[t('search.startDate'), t('search.endDate')]}
            />
          </Form.Item>
          <FeatureGate featureKey="folder_tag" fallback={null}>
            <Form.Item name="tagIds" label={t('search.tag')}>
              <Select
                mode="multiple"
                placeholder={t('search.selectTag')}
                tagRender={tagRender}
                style={{ minWidth: 200 }}
                options={(tags ?? []).map((t) => ({
                  label: t.name,
                  value: t.id,
                }))}
              />
            </Form.Item>
          </FeatureGate>
          <FeatureGate featureKey="advanced_search" fallback={null}>
            <div style={{ width: '100%', marginBottom: 8, marginTop: 8 }}>
              <Typography.Text strong>{t('search.latestScoreTotal')}</Typography.Text>
            </div>
            <Form.Item>
              <Space.Compact>
                <Form.Item name="latestTotalScore" noStyle>
                  <InputNumber
                    min={TOTAL_SCORE_MIN}
                    max={TOTAL_SCORE_MAX}
                    precision={0}
                    placeholder={t('search.totalScorePlaceholder')}
                    style={{ width: 110 }}
                  />
                </Form.Item>
                <Form.Item
                  name="latestTotalScoreCondition"
                  noStyle
                  initialValue={'eq' as TotalScoreCondition}
                >
                  <Select style={{ width: 130 }}>
                    <Option value="eq">{t('search.totalScoreEq')}</Option>
                    <Option value="gte">{t('search.totalScoreGte')}</Option>
                    <Option value="lte">{t('search.totalScoreLte')}</Option>
                  </Select>
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <div style={{ width: '100%', marginBottom: 8, marginTop: 8 }}>
              <Typography.Text strong>{t('search.latestScoreItems')}</Typography.Text>
            </div>
            {ALL_QUESTIONS.map((q, index) => (
              <Form.Item key={q.key} name={`latestScoreQ${index + 1}`} label={q.getLabel(i18n.language)}>
                <Select placeholder={t('search.selectScore')} style={{ width: 110 }} allowClear>
                  <Option value={0}>0 {t('search.score')}</Option>
                  <Option value={1}>1 {t('search.score')}</Option>
                  <Option value={SCORE_AT_LEAST_1}>
                    {t('search.scoreAtLeast1')}
                  </Option>
                  <Option value={2}>2 {t('search.score')}</Option>
                </Select>
              </Form.Item>
            ))}
          </FeatureGate>
          <div style={{ width: '100%', height: 0 }}></div>
        </div>
      </div>

      <div
        style={{
          border: '1px solid #f0f0f0',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#fafafa',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          <Form.Item
            name="ascSort"
            label={t('search.sortAsc')}
            style={{ width: '40%' }}
            // 保持している値のままだと Select がタグのラベルを解決できないため、
            // 表示用に key の配列へ変換して渡す（理由は toSortKeys のコメント参照）
            getValueProps={(value) => ({ value: toSortKeys(value) })}
          >
            <Select
              mode="multiple"
              placeholder={t('search.selectSortAsc')}
              tagRender={tagRender}
              style={{ width: '100%' }}
              onChange={(value) => handleSortChange(value, 'ascSort')}
            >
              {sortFields.map((field) => (
                <Option key={field.key} value={field.key}>
                  {field.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="descSort"
            label={t('search.sortDesc')}
            style={{ width: '40%' }}
            // 昇順側と同じ理由で key の配列へ変換して渡す
            getValueProps={(value) => ({ value: toSortKeys(value) })}
          >
            <Select
              mode="multiple"
              placeholder={t('search.selectSortDesc')}
              tagRender={tagRender}
              style={{ width: '100%' }}
              onChange={(value) => handleSortChange(value, 'descSort')}
            >
              {sortFields.map((field) => (
                <Option key={field.key} value={field.key}>
                  {field.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </div>
      <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Space>
          <Button
            onClick={() => {
              resetSearch();
              onClose();
            }}
            icon={<CloseOutlined />}
          >
            {t('common.close')}
          </Button>
          <Button onClick={resetSearch} icon={<ReloadOutlined />}>
            {t('common.reset')}
          </Button>
          <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
            {t('common.search')}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

/**
 * ソート選択（昇順・降順）が保持しているフォーム値を、Select に渡す value へ変換する。
 *
 * フォームは昇順・降順をまたいだソート優先度を保つため {key, order} の配列を保持しているが、
 * antd v6 の rc-select は value が生の値か {label, value} のときしか options からラベルを
 * 解決できず、{key, order} を渡すとラベルが undefined になり空タグ（×だけ）になってしまう。
 * antd v5 までは value が無いとき key を代わりに使うフォールバックがあったため動いていた。
 * そのため表示用に key の配列へ変換する。フォームが保持する値の形自体は変えない。
 *
 * なお onChange 直後は rc-field-form が Select の生の値（string[]）を一度ストアへ書き込み、
 * その後 handleSortChange が {key, order} の配列へ書き戻すため、文字列が渡ってくる場合も許容する。
 */
const toSortKeys = (value: unknown): string[] =>
  ((value ?? []) as ({ key: string } | string)[]).map((v) =>
    typeof v === 'string' ? v : v.key,
  );

const tagRender = (props: any) => {
  const { label, closable, onClose } = props;
  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Tag
      color="blue"
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      style={{ marginRight: 3 }}
    >
      {label}
    </Tag>
  );
};
